/**
 * A Etapa 7 da Coloração: "Comparativo das Estações", com quantas linhas a consultora quiser
 * (`admin.html:2675-2679`, `5216-5243`).
 *
 * Cada linha é "Campo 1 × Campo 2 = Resultado" com o botão de remover; "+ Adicionar comparativo"
 * põe uma linha vazia no fim. Remover a última linha deixa a lista vazia, como no legado: nada
 * repõe a linha em branco até a aba ser lida de novo.
 */

// O identificador estável do rótulo do grupo.
import { useId } from 'react';
// As peças do Chassi: o botão e o desenho do X.
import { Botao, IconeFechar } from '@complexity/ui';
// O tipo de cada linha, definido nas regras da aba.
import type { LinhaDeComparativo } from './regras.js';

/**
 * O "Remover linha" VERMELHO também em repouso, pela decisão do fundador de 19/09/2026: toda ação de
 * apagar, excluir ou remover é vermelha em todo lugar, como o aviso de perigo do legado. O "✕" do
 * legado é cinza (`--muted`) e só fica vermelho no hover (`admin.html:783-789`); a decisão troca isso
 * de propósito, e a troca está anotada em `progresso/PARIDADE-A7.md` (linha 202).
 *
 * POR QUE O `Botao` NO TAMANHO DE ÍCONE, e não o `BotaoIcone`: o `BotaoIcone` do Chassi só pinta o
 * vermelho no hover e não aceita classe (`packages/ui/src/components/BotaoIcone.tsx:65-66`). O `Botao`
 * aceita, e o `tamanho="icone"` já dá o alvo de 44px nos dois eixos, o mesmo `44px` do legado (`785`).
 *
 * POR QUE O `!`: o `Botao` concatena a classe da variante com esta sem juntar as que disputam a mesma
 * propriedade, e sem o `!` a cor da variante `fantasma` vence pela ordem do CSS. É o mesmo motivo do
 * Excluir de `telas/clientes/index.tsx`. A cor é o token `erro`, o `--erro` do legado nos dois temas
 * (`packages/tokens/src/tokens.css`); o fundo do hover é o `erro-suave`, e o círculo é o
 * `border-radius:50%` do legado.
 */
const CLASSE_DO_REMOVER = '!rounded-full !text-erro hover:!bg-erro-suave';

/** Os três campos de uma linha, na ordem da tela, com o texto de espera do legado (`5222-5226`). */
const CAMPOS = [
  { chave: 'campo1', espera: 'Campo 1' },
  { chave: 'campo2', espera: 'Campo 2' },
  { chave: 'resultado', espera: 'Resultado' },
] as const;

export function Comparativos({
  linhas,
  aoMudar,
  aoRemover,
  aoAdicionar,
  somenteLeitura,
}: {
  readonly linhas: readonly LinhaDeComparativo[];
  // Troca um dos três textos de uma linha, achada pela chave.
  readonly aoMudar: (chave: number, campo: 'campo1' | 'campo2' | 'resultado', valor: string) => void;
  readonly aoRemover: (chave: number) => void;
  readonly aoAdicionar: () => void;
  readonly somenteLeitura: boolean;
}) {
  const idRotulo = useId();
  return (
    // O grupo leva o nome do rótulo, que no legado é um `label` sem `for` (`2676`).
    <div role="group" aria-labelledby={idRotulo} className="flex flex-col gap-cx-2">
      <p id={idRotulo} className="text-cx-sm font-medium text-texto">
        Etapa 7 | Comparativo das Estações
      </p>
      <div className="flex flex-col gap-cx-3">
        {linhas.map((linha, indice) => (
          // `flex-wrap` como o `.coloracao-comparativo-linha` do legado (`admin.html:778-782`): em tela
          // estreita os campos descem para a linha seguinte em vez de estourar a largura. Sem folga à
          // direita: o botão de remover tem 44px de caixa de verdade, e a área de toque não passa dela.
          <div key={linha.chave} className="flex flex-wrap items-center gap-cx-2" data-testid="linha-comparativo">
            {CAMPOS.map((campo, posicao) => (
              <span key={campo.chave} className="contents">
                {/* O "×" e o "=" são o texto da equação do legado, e não ícone: ficam como texto. */}
                {posicao === 1 && (
                  <span aria-hidden="true" className="shrink-0 font-semibold text-texto-suave">
                    ×
                  </span>
                )}
                {posicao === 2 && (
                  <span aria-hidden="true" className="shrink-0 font-semibold text-texto-suave">
                    =
                  </span>
                )}
                {/*
                  PENDÊNCIA DO CHASSI, declarada: o `Campo` de @complexity/ui sempre desenha o rótulo
                  visível, e a linha do legado só tem o texto de espera, repetido em cada linha. Por
                  isso o campo é o `input` com a classe `cx-campo` do próprio Chassi (a mesma aparência
                  do `Campo`), e o nome para o leitor de tela vem do `aria-label`.
                */}
                <input
                  type="text"
                  className="cx-campo min-w-0 flex-[1_1_140px]"
                  placeholder={campo.espera}
                  aria-label={`${campo.espera} da linha ${indice + 1}`}
                  value={linha[campo.chave]}
                  // Consulta: campo desabilitado (`admin.html:3394`).
                  disabled={somenteLeitura}
                  onChange={(evento) => aoMudar(linha.chave, campo.chave, evento.target.value)}
                />
              </span>
            ))}
            {/* Consulta não vê botão de ação (`admin.html:3395`). O X do legado é o "✕" em texto; aqui
                é o desenho do Chassi (divergência aceita pela decisão 1), com o mesmo nome "Remover
                linha" do `title` (`5227`), que vira também o nome para o leitor de tela. */}
            {!somenteLeitura && (
              <Botao
                variante="fantasma"
                tamanho="icone"
                className={CLASSE_DO_REMOVER}
                aria-label="Remover linha"
                title="Remover linha"
                onClick={() => aoRemover(linha.chave)}
              >
                {/* 18px: o "✕" do legado tem `font-size` 20px, e o desenho do X ocupa quase a caixa. */}
                <IconeFechar width={18} height={18} aria-hidden="true" />
              </Botao>
            )}
          </div>
        ))}
      </div>
      {/* "+ Adicionar comparativo" é botão secundário no legado (`2678`), e Consulta não o vê. */}
      {!somenteLeitura && (
        <div>
          <Botao variante="secundario" onClick={aoAdicionar}>
            + Adicionar comparativo
          </Botao>
        </div>
      )}
    </div>
  );
}
